import React, { useState, useEffect } from 'react';
import { getAllLogs, getWeeklyAssessments, getProfile, getMedications, exportAllData, importAllData } from '../db/database';
import { exportToXLSX, exportToCSV, exportJSON } from '../utils/export';
import {
  uploadToGoogleDrive, downloadFromGoogleDrive, getLastSyncTime,
  isGoogleDriveConfigured, initGoogleDrive
} from '../utils/gdrive';
import type { DailyLog, WeeklyAssessment, UserProfile, Medication } from '../types';

export default function ExportPage() {
  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [assessments, setAssessments] = useState<WeeklyAssessment[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [meds, setMeds] = useState<Medication[]>([]);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'success' | 'error'>('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadData();
    setLastSync(getLastSyncTime());
  }, []);

  async function loadData() {
    const [l, a, p, m] = await Promise.all([
      getAllLogs(), getWeeklyAssessments(), getProfile(), getMedications()
    ]);
    setLogs(l);
    setAssessments(a);
    setProfile(p || null);
    setMeds(m);
  }

  const handleExportXLSX = () => {
    exportToXLSX(logs, assessments, profile, meds);
  };

  const handleExportCSV = () => {
    exportToCSV(logs);
  };

  const handleExportJSON = async () => {
    const data = await exportAllData();
    exportJSON(data);
  };

  const handleImportJSON = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await importAllData(data);
      await loadData();
      setSyncMessage('Importazione completata con successo!');
      setSyncStatus('success');
    } catch (err: any) {
      setSyncMessage('Errore durante l\'importazione: ' + err.message);
      setSyncStatus('error');
    }
    setImporting(false);
    // Reset file input
    e.target.value = '';
  };

  const handleGDriveSync = async () => {
    if (!isGoogleDriveConfigured()) {
      setSyncMessage('Configura Google Drive Client ID nelle Impostazioni.');
      setSyncStatus('error');
      return;
    }

    setSyncStatus('syncing');
    setSyncMessage('Sincronizzazione in corso...');

    try {
      await initGoogleDrive();

      // Upload current data
      const data = await exportAllData();
      const result = await uploadToGoogleDrive(data);

      if (result.success) {
        setSyncStatus('success');
        setSyncMessage('Backup caricato su Google Drive!');
        setLastSync(getLastSyncTime());
      } else {
        setSyncStatus('error');
        setSyncMessage(result.error || 'Errore durante il caricamento');
      }
    } catch (err: any) {
      setSyncStatus('error');
      setSyncMessage(err.message);
    }
  };

  const handleGDriveRestore = async () => {
    if (!isGoogleDriveConfigured()) {
      setSyncMessage('Configura Google Drive Client ID nelle Impostazioni.');
      setSyncStatus('error');
      return;
    }

    setSyncStatus('syncing');
    setSyncMessage('Scaricamento backup...');

    try {
      await initGoogleDrive();
      const result = await downloadFromGoogleDrive();

      if (result.success && result.data) {
        await importAllData(result.data);
        await loadData();
        setSyncStatus('success');
        setSyncMessage('Dati ripristinati da Google Drive!');
      } else {
        setSyncStatus('error');
        setSyncMessage(result.error || 'Nessun backup trovato');
      }
    } catch (err: any) {
      setSyncStatus('error');
      setSyncMessage(err.message);
    }
  };

  return (
    <div className="page">
      <h1 className="page-title">Esporta e Sincronizza</h1>

      {/* Stats */}
      <div className="card">
        <div className="card-title">I tuoi dati</div>
        <div style={{ display: 'flex', justifyContent: 'space-around' }}>
          <StatItem label="Registrazioni" value={logs.length} />
          <StatItem label="Valutazioni" value={assessments.length} />
          <StatItem label="Farmaci" value={meds.length} />
        </div>
      </div>

      {/* Export */}
      <div className="card">
        <div className="card-title">Esporta per l'allergologo</div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Formato italiano: date dd/mm/yyyy, virgola decimale, separatore punto e virgola (CSV).
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-primary btn-full" onClick={handleExportXLSX} disabled={logs.length === 0}>
            Esporta XLSX (Excel)
          </button>
          <button className="btn btn-secondary btn-full" onClick={handleExportCSV} disabled={logs.length === 0}>
            Esporta CSV
          </button>
          <button className="btn btn-secondary btn-full" onClick={handleExportJSON}>
            Esporta Backup JSON
          </button>
        </div>
      </div>

      {/* Import */}
      <div className="card">
        <div className="card-title">Importa dati</div>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
          Importa un backup JSON precedentemente esportato. I dati esistenti verranno aggiornati.
        </p>
        <label className="btn btn-secondary btn-full" style={{ cursor: 'pointer' }}>
          {importing ? 'Importazione...' : 'Importa Backup JSON'}
          <input type="file" accept=".json" onChange={handleImportJSON} style={{ display: 'none' }} />
        </label>
      </div>

      {/* Google Drive */}
      <div className="card">
        <div className="card-title">Google Drive Sync</div>
        {!isGoogleDriveConfigured() ? (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            <p style={{ marginBottom: 8 }}>
              Per abilitare la sincronizzazione con Google Drive, configura il Client ID OAuth nelle Impostazioni.
            </p>
            <p>
              Istruzioni: Vai su console.cloud.google.com, crea un progetto, abilita Google Drive API,
              crea credenziali OAuth 2.0 per applicazione web, e inserisci il Client ID nelle impostazioni.
            </p>
          </div>
        ) : (
          <>
            {lastSync && (
              <div className="sync-status" style={{ marginBottom: 12 }}>
                <div className={`sync-dot ${syncStatus === 'syncing' ? 'syncing' : 'synced'}`} />
                <span>Ultimo sync: {new Date(lastSync).toLocaleString('it-IT')}</span>
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button className="btn btn-primary btn-full" onClick={handleGDriveSync}
                disabled={syncStatus === 'syncing'}>
                Carica backup su Google Drive
              </button>
              <button className="btn btn-secondary btn-full" onClick={handleGDriveRestore}
                disabled={syncStatus === 'syncing'}>
                Ripristina da Google Drive
              </button>
            </div>
          </>
        )}

        {syncMessage && (
          <div style={{
            marginTop: 12, padding: 10, borderRadius: 8, fontSize: 12,
            background: syncStatus === 'success' ? 'rgba(102, 187, 106, 0.1)' : syncStatus === 'error' ? 'rgba(239, 83, 80, 0.1)' : 'rgba(79, 195, 247, 0.1)',
            color: syncStatus === 'success' ? 'var(--success)' : syncStatus === 'error' ? 'var(--danger)' : 'var(--accent)',
            border: `1px solid ${syncStatus === 'success' ? 'var(--success)' : syncStatus === 'error' ? 'var(--danger)' : 'var(--accent)'}`,
          }}>
            {syncMessage}
          </div>
        )}
      </div>
    </div>
  );
}

function StatItem({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--accent)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</div>
    </div>
  );
}
